import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Upload, Loader2, Download, CheckCircle2, AlertTriangle, XCircle, Plus, ArrowRight } from "lucide-react";

/**
 * ExcelPartyImportDialog — evrensel Excel'den Müşteri / Tedarikçi aktarımı.
 *
 * kind="customers" | "suppliers"
 * Akış: Dosya Seç → Analiz (header otomatik tespit) → Kolon Eşleştirme →
 * Önizleme & Duplicate Kontrolü → Kullanıcı Onayı → Import → Sonuç Raporu.
 * Tanınmayan kolonlar "Yok Say" — import'u durdurmaz.
 */
const FIELD_OPTIONS = [
  { value: "", label: "Yok Say" },
  { value: "name", label: "Ünvan" },
  { value: "code", label: "Kod" },
  { value: "tax_no", label: "Vergi No / TCKN" },
  { value: "tax_office", label: "Vergi Dairesi" },
  { value: "address", label: "Adres" },
  { value: "city", label: "Şehir" },
  { value: "district", label: "İlçe" },
  { value: "phone", label: "Telefon" },
  { value: "email", label: "E-posta" },
  { value: "contact_person", label: "Yetkili Kişi" },
  { value: "category", label: "Kategori" },
  { value: "balance", label: "Bakiye" },
  { value: "bh_id", label: "Bizim Hesap ID" },
];

export default function ExcelPartyImportDialog({ kind, open, onClose, onImported }) {
  const label = kind === "suppliers" ? "Tedarikçi" : "Müşteri";
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);       // {header_row, columns, mapping_source, total_rows}
  const [mapping, setMapping] = useState({});           // colIndex(str) -> field
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decisions, setDecisions] = useState({});       // row -> {action, target_id}

  useEffect(() => {
    if (open) {
      setFile(null); setAnalysis(null); setMapping({}); setPreview(null);
      setResult(null); setDecisions({}); setBusy(false);
    }
  }, [open]);

  const analyzeFile = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/parties-import/analyze/${kind}`, fd);
      setAnalysis(data);
      const m = {};
      (data.columns || []).forEach((c) => { m[String(c.index)] = c.field || ""; });
      setMapping(m);
    } catch (e) {
      toast.error(formatApiError(e));
      setFile(null);
    } finally { setBusy(false); }
  };

  const pickFile = (f) => {
    setFile(f || null);
    setAnalysis(null); setPreview(null); setResult(null);
    if (f) analyzeFile(f);
  };

  const mappingJson = () => {
    const m = {};
    Object.entries(mapping).forEach(([k, v]) => { if (v) m[k] = v; });
    return JSON.stringify(m);
  };

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", mappingJson());
      fd.append("header_row", String((analysis?.header_row || 1) - 1));
      const { data } = await api.post(`/parties-import/preview/${kind}`, fd);
      setPreview(data);
      const d = {};
      (data.items || []).forEach((it) => {
        if (it.status === "suspicious") d[it.row] = { action: "skip", target_id: it.candidates?.[0]?.id };
      });
      setDecisions(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!file || !preview) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("decisions", JSON.stringify(decisions));
      fd.append("mapping", mappingJson());
      fd.append("header_row", String((analysis?.header_row || 1) - 1));
      const { data } = await api.post(`/parties-import/apply/${kind}`, fd);
      setResult(data);
      onImported?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadBlob = async (promise, filename) => {
    try {
      const { data } = await promise;
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadErrors = () => {
    const bad = (result?.results || []).filter((r) => r.status === "error" || r.status === "skipped");
    if (!bad.length) { toast.error("İndirilecek hatalı/atlanan satır yok"); return; }
    downloadBlob(api.post("/parties-import/error-report", { kind, rows: bad }, { responseType: "blob" }), `${kind}_aktarim_raporu.xlsx`);
  };

  const downloadTemplate = () => downloadBlob(
    api.get(`/parties-import/template/${kind}`, { responseType: "blob" }), `${kind}_sablon.xlsx`);

  const c = preview?.counts || {};
  const suspiciousItems = (preview?.items || []).filter((i) => i.status === "suspicious");
  const undecided = suspiciousItems.filter((i) => (decisions[i.row]?.action || "skip") === "skip").length;
  const nameMapped = Object.values(mapping).includes("name");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden bg-slate-900 border-slate-800" data-testid="excel-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight">Excel'den {label} Aktar</DialogTitle>
        </DialogHeader>

        {/* ADIM 1 — Dosya seçimi */}
        {!analysis && !preview && !result && (
          <div className="space-y-4">
            <div className="text-[11px] text-slate-500">
              Bizim Hesap, Logo, Mikro veya başka bir programdan alınmış .xlsx / .xls dosyası yükleyin.
              Başlık satırı otomatik tespit edilir; kolonları sonraki adımda eşleştirirsiniz.
              Onay vermeden veritabanına yazılmaz.
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="excel-import-file-input"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="excel-import-pick-btn"
                className="rounded-sm border-slate-800" disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {file ? file.name : "Dosya Seç"}
              </Button>
              <Button variant="ghost" onClick={downloadTemplate} data-testid="excel-import-template-btn"
                className="rounded-sm text-slate-400">
                <Download className="w-4 h-4 mr-2" /> Örnek Şablon İndir
              </Button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose} className="rounded-sm">İptal</Button>
            </DialogFooter>
          </div>
        )}

        {/* ADIM 2 — Kolon Eşleştirme */}
        {analysis && !preview && !result && (
          <div className="flex-1 min-h-0 flex flex-col space-y-3">
            <div className="text-[11px] text-slate-500 shrink-0">
              Başlık satırı: <span className="text-slate-300 font-mono">{analysis.header_row}. satır</span>
              {" · "}{analysis.total_rows} veri satırı bulundu
              {analysis.mapping_source === "saved" && (
                <span className="ml-2 text-emerald-400">(önceki eşleştirmeniz önerildi)</span>
              )}
              {!nameMapped && (
                <span className="ml-2 text-amber-400" data-testid="excel-import-name-missing">
                  Ünvan kolonu otomatik bulunamadı — devam etmek için bir kolonu Ünvan olarak eşleştirin.
                </span>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto border border-slate-800 rounded-sm">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-950/95">
                  <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-3 py-2">Excel Kolonu</th>
                    <th className="text-left px-3 py-2">Örnek Değer</th>
                    <th className="text-left px-3 py-2 w-52">ACUTO Alanı</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(analysis.columns || []).map((col) => (
                    <tr key={col.index} className="text-slate-300" data-testid={`excel-import-col-${col.index}`}>
                      <td className="px-3 py-2 font-medium">{col.header}</td>
                      <td className="px-3 py-2 text-slate-500 text-[11px] truncate max-w-[220px]">{col.sample || "—"}</td>
                      <td className="px-3 py-2">
                        <select
                          data-testid={`excel-import-map-${col.index}`}
                          value={mapping[String(col.index)] || ""}
                          onChange={(e) => setMapping((m) => ({ ...m, [String(col.index)]: e.target.value }))}
                          className="w-full bg-slate-950 border border-slate-800 rounded-sm px-2 py-1.5 text-xs text-slate-200"
                        >
                          {FIELD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="shrink-0">
              <Button variant="ghost" onClick={() => { setAnalysis(null); setFile(null); }} className="rounded-sm">Geri</Button>
              <Button onClick={doPreview} disabled={!nameMapped || busy} data-testid="excel-import-preview-btn"
                className="bg-blue-600 hover:bg-blue-500 rounded-sm">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Önizleme ve Eşleştirme
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ADIM 3 — Önizleme */}
        {preview && !result && (
          <div className="flex-1 min-h-0 flex flex-col space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-xs shrink-0">
              <Stat label="Toplam Kayıt" value={preview.total || 0} tone="slate" testid="excel-import-total" />
              <Stat label="Eşleşen" value={c.matched || 0} tone="blue" testid="excel-import-matched" />
              <Stat label="Yeni Oluşturulacak" value={c.new || 0} tone="emerald" testid="excel-import-new" />
              <Stat label="Şüpheli Eşleşme" value={c.suspicious || 0} tone="amber" testid="excel-import-suspicious" />
              <Stat label="Hatalı / Eksik" value={c.error || 0} tone="rose" testid="excel-import-error" />
            </div>
            {suspiciousItems.length > 0 && (
              <div className="p-2 border border-amber-900/60 bg-amber-950/20 rounded-sm text-[11px] text-amber-300 shrink-0" data-testid="excel-import-suspicious-warning">
                <strong>{suspiciousItems.length} satır</strong> yalnızca ünvan benzerliğiyle eşleşiyor — kesin eşleşme sayılmadı.
                Her satır için "Eşleştir / Yeni Oluştur / Atla" kararı verin. Karar verilmeyen satırlar atlanır (şu an atlanacak: {undecided}).
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto border border-slate-800 rounded-sm">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-950/95">
                  <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-3 py-2 w-10">Satır</th>
                    <th className="text-left px-3 py-2">Durum</th>
                    <th className="text-left px-3 py-2">Ünvan</th>
                    <th className="text-left px-3 py-2">Vergi No</th>
                    <th className="text-left px-3 py-2">Detay</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(preview.items || []).map((it) => (
                    <tr key={it.row} className="text-slate-300" data-testid={`excel-import-row-${it.row}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{it.row}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {it.status === "matched" && <span className="text-blue-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Eşleşti</span>}
                        {it.status === "new" && <span className="text-emerald-400 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Yeni</span>}
                        {it.status === "suspicious" && <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Kontrol Gerekli</span>}
                        {it.status === "error" && <span className="text-rose-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Hatalı</span>}
                      </td>
                      <td className="px-3 py-2">{it.name}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-400">{it.tax_no || "—"}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-400">
                        {it.status === "matched" && <span>→ {it.matched_name} <span className="text-slate-600">({it.match_via})</span></span>}
                        {it.status === "error" && <span className="text-rose-400">{it.reason}</span>}
                        {it.status === "suspicious" && (
                          <span className="flex items-center gap-1 flex-wrap">
                            <span>aday → <span className="text-slate-200">{it.candidates?.[0]?.name}</span></span>
                            {["link", "new", "skip"].map((a) => (
                              <button key={a} type="button"
                                data-testid={`excel-import-dec-${it.row}-${a}`}
                                onClick={() => setDecisions((d) => ({ ...d, [it.row]: { action: a, target_id: it.candidates?.[0]?.id } }))}
                                className={`px-2 py-0.5 rounded-sm border text-[10px] ${
                                  (decisions[it.row]?.action || "skip") === a
                                    ? a === "link" ? "bg-blue-600 border-blue-500 text-white"
                                      : a === "new" ? "bg-emerald-600 border-emerald-500 text-white"
                                      : "bg-slate-600 border-slate-500 text-white"
                                    : "border-slate-700 text-slate-400 hover:bg-slate-900"}`}>
                                {a === "link" ? "Eşleştir" : a === "new" ? "Yeni Oluştur" : "Atla"}
                              </button>
                            ))}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="shrink-0">
              <Button variant="ghost" onClick={() => setPreview(null)} className="rounded-sm">Geri</Button>
              <Button onClick={doApply} disabled={busy || ((c.matched || 0) + (c.new || 0) + suspiciousItems.filter((i) => decisions[i.row]?.action !== "skip").length) === 0}
                data-testid="excel-import-apply-btn" className="bg-emerald-600 hover:bg-emerald-500 rounded-sm">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Aktarımı Uygula
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ADIM 4 — Sonuç */}
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs">
              <Stat label="Eşleştirildi" value={result.counts?.matched || 0} tone="blue" testid="excel-import-res-matched" />
              <Stat label="Yeni Oluşturuldu" value={result.counts?.created || 0} tone="emerald" testid="excel-import-res-created" />
              <Stat label="Atlandı" value={result.counts?.skipped || 0} tone="slate" testid="excel-import-res-skipped" />
              <Stat label="Hata" value={result.counts?.error || 0} tone="rose" testid="excel-import-res-error" />
            </div>
            {((result.counts?.error || 0) + (result.counts?.skipped || 0)) > 0 && (
              <Button variant="outline" onClick={downloadErrors} data-testid="excel-import-errors-download"
                className="rounded-sm border-slate-800">
                <Download className="w-4 h-4 mr-2" /> Hatalı / Atlanan Satırları Excel İndir
              </Button>
            )}
            <DialogFooter>
              <Button onClick={() => { onClose?.(); }} data-testid="excel-import-close-btn"
                className="bg-blue-600 hover:bg-blue-500 rounded-sm">Kapat</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneCls = {
    emerald: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30",
    blue: "text-blue-400 border-blue-900/50 bg-blue-950/30",
    slate: "text-slate-400 border-slate-800 bg-slate-950/40",
    amber: "text-amber-400 border-amber-900/50 bg-amber-950/30",
    rose: "text-rose-400 border-rose-900/50 bg-rose-950/30",
  }[tone] || "text-slate-400 border-slate-800";
  return (
    <div className={`rounded-sm border px-2 py-1.5 ${toneCls}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-mono">{value}</div>
    </div>
  );
}
